import React, { useEffect, useMemo, useRef, useState } from 'react';
import { camelCase } from 'lodash';
import styled from 'styled-components';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { AppSnackbar } from '@sierralabs/core-ui';
import { isRelationColumn } from '../common/EntityRelationField';
import { EntitySampleRelationConfigurator, EntitySampleRelationValidator } from '../common/EntitySampleRelationConfigurator';
import { useEntityContext } from '../EntityContext';
import { useGenerateData } from '../hooks/useGenerateData';
const StyledTableRow = styled(TableRow) `
  .MuiTableCell-root {
    vertical-align: top;
  }
`;
const CreateEntitySampleDataDialog = props => {
    const { open, entity, onCancel, onGenerate } = props;
    const onGenerateRef = useRef(onGenerate);
    const { plugins } = useEntityContext();
    const [total, setTotal] = useState(10);
    // initialize sampleData configuration
    const [rows, setRows] = useState(() => {
        return entity.columns.map(column => {
            var _a, _b, _c;
            const isRelation = isRelationColumn(entity, column);
            const plugin = plugins === null || plugins === void 0 ? void 0 : plugins.find(plugin => plugin.type === column.type);
            const Configurator = isRelation
                ? EntitySampleRelationConfigurator
                : (_a = plugin === null || plugin === void 0 ? void 0 : plugin.sampleData) === null || _a === void 0 ? void 0 : _a.configurator;
            const validate = isRelation
                ? EntitySampleRelationValidator
                : (_b = plugin === null || plugin === void 0 ? void 0 : plugin.sampleData) === null || _b === void 0 ? void 0 : _b.validate;
            return {
                column,
                plugin,
                validate,
                Configurator,
                config: isRelation
                    ? { relation: null }
                    : (_c = plugin === null || plugin === void 0 ? void 0 : plugin.sampleData) === null || _c === void 0 ? void 0 : _c.defaultValue(column)
            };
        });
    });
    const { data: generatedData, isLoading, error, generateData } = useGenerateData(entity.id || '');
    const isValid = useMemo(() => {
        // valid if no errors
        return rows.every(row => {
            const { column, config, validate } = row;
            return !validate || !validate(config, column);
        });
    }, [rows]);
    useEffect(() => {
        var _a;
        if (generatedData) {
            (_a = onGenerateRef.current) === null || _a === void 0 ? void 0 : _a.call(onGenerateRef);
        }
    }, [generatedData]);
    return (React.createElement(Dialog, { fullWidth: true, open: open, maxWidth: 'md', onClose: () => !isLoading && (onCancel === null || onCancel === void 0 ? void 0 : onCancel()) },
        React.createElement(DialogTitle, null, "Create Sample Data"),
        React.createElement(DialogContent, { style: { paddingLeft: 0, paddingRight: 0 } },
            React.createElement(Box, { mx: 3 },
                React.createElement(TextField, { fullWidth: true, size: 'small', type: 'number', variant: 'outlined', helperText: 'Set the number of records to create', value: total, disabled: isLoading, onChange: event => {
                        const value = parseInt(event.target.value);
                        if (isFinite(value) && value > 0 && value <= 50000) {
                            setTotal(value);
                        }
                    } })),
            React.createElement(TableContainer, null,
                React.createElement(Table, null,
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, null, "Column"),
                            React.createElement(TableCell, null, "Type"),
                            React.createElement(TableCell, null, "Sample Data"))),
                    React.createElement(TableBody, null, rows.map((row, index) => {
                        const { column, config, plugin, Configurator, validate } = row;
                        return (React.createElement(StyledTableRow, { key: `${column.name}-${index}` },
                            React.createElement(TableCell, null, column.displayName),
                            React.createElement(TableCell, null, column.type),
                            React.createElement(TableCell, null, column.generated ? (React.createElement(Typography, { color: 'textSecondary' }, "Auto generated field.")) : plugin && Configurator ? (React.createElement(Configurator, { plugin: plugin, entity: entity, column: column, config: config, disabled: isLoading, validate: validate, onChange: updatedConfig => {
                                    setRows(rows => {
                                        rows[index] = Object.assign(Object.assign({}, rows[index]), { config: Object.assign(Object.assign({}, config), updatedConfig) });
                                        return [...rows];
                                    });
                                } })) : (React.createElement(Typography, { color: 'textSecondary' }, "No options for this type.")))));
                    }))))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { disabled: isLoading, onClick: onCancel }, "Cancel"),
            React.createElement(Button, { color: 'primary', variant: 'contained', disabled: !isValid || isLoading, onClick: () => {
                    const data = rows.map(({ column, config }) => {
                        const name = column.name || camelCase(column.displayName);
                        return { name, type: column.type, config };
                    });
                    generateData(total, data);
                } },
                "Create ",
                total,
                " Record",
                total !== 1 && 's')),
        React.createElement(AppSnackbar, { open: !!error, severity: 'error' }, error === null || error === void 0 ? void 0 : error.message)));
};
export default CreateEntitySampleDataDialog;
//# sourceMappingURL=CreateEntitySampleDataDialog.js.map